import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { AlertTriangle, ArrowLeft, Link2, Mail, Pencil, Phone, Trash2, X } from 'lucide-react';
import { useI18n } from '../contexts/I18nContext';
import { usePermissions } from '../hooks/usePermissions';
import { useActor, useDeleteActor, useUnlinkActorClient, useUpdateActor } from '../hooks/useActors';
import { useConfirm } from '../components/ui/ConfirmProvider';
import { Avatar } from '../components/tasks/linear/Avatar';
import { ActorFormModal } from '../components/actors/ActorFormModal';
import { ConflictBadge } from '../components/actors/ConflictBadge';
import { LinkClientDialog } from '../components/actors/LinkClientDialog';
import { ClientIndustryPicker } from '../components/actors/ClientIndustryPicker';
import { FadeIn, SkeletonTable } from '../components/ui/Skeleton';

/** One actor: contact, notes, and every client they appeared at. */
export function ActorDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { t } = useI18n();
  const { can } = usePermissions();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const { data: actor, isLoading, isError } = useActor(id);
  const update = useUpdateActor(id ?? '');
  const remove = useDeleteActor();
  const unlink = useUnlinkActorClient();
  const [editing, setEditing] = useState(false);
  const [linking, setLinking] = useState(false);
  const canEdit = can('actors', 'canEdit');

  if (isLoading) {
    return (
      <div className="p-4 sm:p-6 lg:p-8">
        <SkeletonTable />
      </div>
    );
  }
  if (isError || !actor) {
    return <p className="p-8 text-sm text-red-600">{t('common.loadError')}</p>;
  }

  // The actor's industries come from their clients (the industry is the client's).
  const industries = [
    ...new Map(
      actor.clients.filter((l) => l.industry).map((l) => [l.industry!.id, l.industry!.name]),
    ).values(),
  ];
  // Clients in an industry where the actor is at two or more of them.
  const conflictClientIds = new Set(
    actor.conflicts.flatMap((g) => g.clients.map((c) => c.clientId)),
  );

  return (
    <div className="w-full p-4 sm:p-6 lg:p-8">
      <Link
        to="/actors"
        className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800"
      >
        <ArrowLeft size={14} /> {t('actors.detail.back')}
      </Link>

      <div className="mt-4 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-center gap-4">
          <Avatar name={actor.fullName} size={64} src={actor.avatarUrl} />
          <div>
            <h1 className="flex flex-wrap items-center gap-2 text-2xl font-bold text-gray-900">
              {actor.fullName}
              <ConflictBadge groups={actor.conflicts} />
            </h1>
            {industries.length > 0 && (
              <p className="mt-1 text-sm text-gray-600">
                {t('actors.detail.industries')}: {industries.join(', ')}
              </p>
            )}
            <div className="mt-1 flex flex-wrap gap-3 text-sm text-gray-600">
              {actor.phone && (
                <a
                  href={`tel:${actor.phone}`}
                  className="inline-flex items-center gap-1 hover:underline"
                >
                  <Phone size={14} /> {actor.phone}
                </a>
              )}
              {actor.email && (
                <a
                  href={`mailto:${actor.email}`}
                  className="inline-flex items-center gap-1 hover:underline"
                >
                  <Mail size={14} /> {actor.email}
                </a>
              )}
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          {canEdit && (
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="inline-flex items-center gap-1.5 rounded border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              <Pencil size={14} /> {t('actors.detail.edit')}
            </button>
          )}
          {can('actors', 'canDelete') && (
            <button
              type="button"
              onClick={async () => {
                const ok = await confirm({
                  title: t('actors.detail.delete'),
                  message: t('actors.detail.deleteConfirm', { name: actor.fullName }),
                  danger: true,
                });
                if (!ok) return;
                await remove.mutateAsync(actor.id);
                navigate('/actors');
              }}
              className="inline-flex items-center gap-1.5 rounded border border-red-200 px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50"
            >
              <Trash2 size={14} /> {t('actors.detail.delete')}
            </button>
          )}
        </div>
      </div>

      {actor.notes && (
        <p className="mt-4 whitespace-pre-wrap rounded-lg border border-gray-200 bg-card p-4 text-sm text-gray-700">
          {actor.notes}
        </p>
      )}

      <section className="mt-6 rounded-lg border border-gray-200 bg-card">
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-200 px-4 py-3">
          <h2 className="text-sm font-semibold text-gray-900">
            {t('actors.detail.clients')} · {actor.clients.length}
          </h2>
          {canEdit && (
            <button
              type="button"
              onClick={() => setLinking(true)}
              className="inline-flex items-center gap-1.5 rounded bg-brand-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-700"
            >
              <Link2 size={14} /> {t('actors.detail.linkClient')}
            </button>
          )}
        </div>
        {actor.clients.length === 0 ? (
          <p className="p-6 text-center text-sm text-gray-500">{t('actors.noClients')}</p>
        ) : (
          <FadeIn>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-xs uppercase tracking-wide text-gray-500">
                  <tr>
                    <th className="px-4 py-2">{t('actors.col.client')}</th>
                    <th className="px-4 py-2">{t('actors.col.industry')}</th>
                    <th className="px-4 py-2">{t('actors.col.usedAt')}</th>
                    <th className="px-4 py-2">{t('actors.col.note')}</th>
                    <th className="px-4 py-2" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {actor.clients.map((l) => (
                    <tr
                      key={l.clientId}
                      className={conflictClientIds.has(l.clientId) ? 'bg-amber-500/10' : ''}
                    >
                      <td className="px-4 py-2">
                        <Link
                          to={`/clients/${l.clientId}`}
                          className="font-medium text-brand-700 hover:underline"
                        >
                          {l.clientName}
                        </Link>
                        {l.conflictAcknowledgedAt && (
                          <div
                            className="mt-0.5 flex items-center gap-1 text-xs text-amber-700"
                            title={l.conflictWith.join(', ')}
                          >
                            <AlertTriangle size={12} />
                            {t('actors.acknowledged', {
                              name: l.conflictAcknowledgedByName ?? '—',
                              date: l.conflictAcknowledgedAt.slice(0, 10),
                            })}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-2 text-gray-600">
                        {l.industry ? (
                          l.industry.name
                        ) : can('clients', 'canEdit') ? (
                          <ClientIndustryPicker clientId={l.clientId} className="w-48" />
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="px-4 py-2 text-gray-600">{l.usedAt}</td>
                      <td className="px-4 py-2 text-gray-600">{l.note ?? '—'}</td>
                      <td className="px-4 py-2 text-right">
                        {canEdit && (
                          <button
                            type="button"
                            aria-label={t('actors.unlink')}
                            title={t('actors.unlink')}
                            onClick={async () => {
                              const ok = await confirm({
                                message: t('actors.unlinkConfirm', { client: l.clientName }),
                                danger: true,
                              });
                              if (ok)
                                await unlink.mutateAsync({
                                  actorId: actor.id,
                                  clientId: l.clientId,
                                });
                            }}
                            className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-red-600"
                          >
                            <X size={16} />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </FadeIn>
        )}
      </section>

      {editing && (
        <ActorFormModal
          initial={actor}
          submitting={update.isPending}
          onClose={() => setEditing(false)}
          onSubmit={async (input) => {
            await update.mutateAsync(input);
            setEditing(false);
          }}
        />
      )}
      {linking && (
        <LinkClientDialog
          actor={{ id: actor.id, fullName: actor.fullName }}
          onClose={() => setLinking(false)}
        />
      )}
    </div>
  );
}
